import { useCallback, useState } from 'react'
import type { ReservationHoldErrorResponse } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useWatchMany } from '@sevenrooms/form'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly, Formats, legacyJSDateAdapter } from '@sevenrooms/timepiece'
import { Form } from '@sevenrooms/ui-kit/form'
import { VStack, Window } from '@sevenrooms/ui-kit/layout'
import { ReservationHoldModal } from '../../../components/Modals/ResrvationHoldModal'
import {
  type AvailabilityTimeWithUpSellCost,
  useCachedCreateReservationHold,
  useExperiencesWithAvailability,
  useReservationNavigation,
  useReservationsRoute,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../../hooks'
import { useModals, useReservationFormState } from '../../../store'
import {
  confirmTimeslotExperience,
  finishSearchPage,
  selectTimeslot,
  updateDateSearchParams,
  updatePartySizeSearchParams,
  updateTimeSearchParams,
} from '../../../utils'
import { useListAvailabilityDatesQuery } from '../AvailabilityDateApi'
import { ExperienceDetailsModal } from '../ExperienceDetailsModal'
import { Experiences } from '../Experiences'
import { ReservationSearchComponent } from '../ReservationSearchComponent'
import { StyledSearchInputGridContainer } from '../ReservationSearchInputGroup'
import { useSearchForm } from '../useSearchForm'
import { getReservationHoldErrorMessage } from '../utils'
import type { ExperienceWithAvailability } from '../../../hooks/useAvailability'

interface ExperiencesTabProps {
  showTabs?: boolean
}

export function ExperiencesTab({ showTabs }: ExperiencesTabProps) {
  const { formatMessage } = useLocales()
  const { urlKey, currencyCode, id: venueId, startOfDayTime } = useVenue()
  const { activeModal, previousActiveModal, showModal, hideModal, showErrorModal } = useModals()
  const { navigateNext: navigateNextStep, disableStep, enableStep } = useReservationNavigation()
  const { maxDaysOut, reservationHoldEnabled } = useWidgetSettings()
  const { selectedLanguage } = useWidgetLanguage()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<AvailabilityTimeWithUpSellCost>()
  const [selectedExperience, setSelectedExperience] = useState<ExperienceWithAvailability>()
  const { updateQuery, channel } = useReservationsRoute()

  const { isFetching: isDatesFetching, data: availabilityDates = [] } = useListAvailabilityDatesQuery({
    venue: urlKey,
    numDays: maxDaysOut,
    startDate: legacyJSDateAdapter(new Date(), Formats.MonthDayYearByDash),
  })

  const [createReservationHold] = useCachedCreateReservationHold()

  const isDayBlocked = useCallback((date: Date) => !availabilityDates.includes(DateOnly.fromDate(date).toIso()), [availabilityDates])

  const {
    formState: { ...searchFormData },
    updateFormState,
  } = useReservationFormState()

  const form = useSearchForm({
    partySize: searchFormData.partySize,
    startDate: searchFormData.startDate,
    startTime: searchFormData.startTime,
    haloTimeIntervalMinutes: searchFormData.haloTimeIntervalMinutes,
  })
  const { field, setValue, handleSubmit } = form
  const [partySize, startDate, startTime, haloTimeIntervalMinutes] = useWatchMany(field, [
    'partySize',
    'startDate',
    'startTime',
    'haloTimeIntervalMinutes',
  ])

  const {
    data: experiences,
    isFetching: isExperiencesFetching,
    isError,
  } = useExperiencesWithAvailability({
    venue: venueId,
    partySize: partySize || undefined,
    startDate: startDate ? DateOnly.fromDate(startDate).toIso() : undefined,
    startTime,
    haloTimeIntervalMinutes,
    startOfDayTime,
    clientId: searchFormData.clientId,
    selectedLangCode: selectedLanguage,
    experienceType: 'EXPERIENCE',
  })

  const onTimeSlotClick = useCallback(
    async (timeSlot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) => {
      await handleSubmit(data => {
        const { partySize, startDate } = data
        if (!partySize || !startDate) {
          return
        }
        const date = DateOnly.fromDate(startDate).toIso()

        // update state when other availability is selected.
        updateFormState({
          partySize,
          startDate: date,
          selectedTimeSlot: timeSlot,
          selectedExperience: experience,
        })
        selectTimeslot(urlKey, date, timeSlot.time, partySize)
        if (timeSlot.upsellCategories && timeSlot.upsellCategories.length > 0) {
          enableStep('upgrades')
        } else {
          disableStep('upgrades')
        }
      })()
      setSelectedTimeSlot(timeSlot)
      setSelectedExperience(experience)
      showModal('moreExperienceDetails')
    },
    [disableStep, enableStep, handleSubmit, showModal, updateFormState, urlKey]
  )

  const onMoreDetailsClick = useCallback(
    (experience: ExperienceWithAvailability) => {
      setSelectedExperience(experience)
      showModal('moreExperienceDetails')
    },
    [showModal]
  )

  const onClearTimeFilter = useCallback(() => {
    updateQuery({ time: ReservationWidget.AllTimesOption })
    setValue('startTime', ReservationWidget.AllTimesOption)
  }, [setValue, updateQuery])

  const navigateNext = useCallback(
    async (timeslot: AvailabilityTimeWithUpSellCost) => {
      const { accessPersistentId, shiftPersistentId, time, timeIso } = timeslot
      const date = DateOnly.from(timeIso).toIso()
      finishSearchPage(urlKey)
      if (reservationHoldEnabled) {
        showModal('reservationHold')
        try {
          await createReservationHold({
            accessPersistentId,
            channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
            date,
            partySize: partySize as number,
            shiftPersistentId,
            time,
            venueId,
            trackingSlug: searchFormData.trackingSlug,
            clientId: searchFormData.clientId,
          }).unwrap()
        } catch (err) {
          const errorMessage = getReservationHoldErrorMessage(err as ReservationHoldErrorResponse, formatMessage)
          showErrorModal(errorMessage, undefined, true)
        }
        hideModal()
      }
      navigateNextStep()
    },
    [
      urlKey,
      reservationHoldEnabled,
      navigateNextStep,
      showModal,
      hideModal,
      createReservationHold,
      channel,
      partySize,
      venueId,
      searchFormData.trackingSlug,
      searchFormData.clientId,
      formatMessage,
      showErrorModal,
    ]
  )

  const onAvailabilitySelect = useCallback(
    (timeSlot: AvailabilityTimeWithUpSellCost) => {
      navigateNext(timeSlot)
      const startDate = DateOnly.from(timeSlot.timeIso).toIso()
      confirmTimeslotExperience(urlKey, startDate, timeSlot.time, partySize)
    },
    [navigateNext, partySize, urlKey]
  )

  const onMoreDetailsModalClose = useCallback(() => {
    hideModal()
    setSelectedExperience(undefined)
    setSelectedTimeSlot(undefined)
  }, [hideModal])

  const onPartySizeChange = useCallback(
    (value: number) => {
      updateFormState({ partySize: value })
      updatePartySizeSearchParams(urlKey, value)
      updateQuery({ partySize: value?.toString() })
    },
    [updateFormState, updateQuery, urlKey]
  )

  const onExperiencePartySizeChange = useCallback(
    (value: number) => {
      setValue('partySize', value)
      onPartySizeChange(value)
    },
    [onPartySizeChange, setValue]
  )

  const onStartDateChange = useCallback(
    (value: Date) => {
      const dateIso = DateOnly.fromDate(value).toIso()
      updateFormState({ startDate: dateIso })
      updateQuery({ date: dateIso })
      updateDateSearchParams(urlKey, DateOnly.fromDate(value).toIso())
    },
    [updateFormState, updateQuery, urlKey]
  )
  const onExperienceStartDateChange = useCallback(
    (value: Date) => {
      setValue('startDate', value)
      onStartDateChange(value)
    },
    [onStartDateChange, setValue]
  )

  const handleFilterTimeChange = ({ startTime, haloTimeIntervalMinutes }: { startTime: string; haloTimeIntervalMinutes: number }) => {
    setValue('startTime', startTime)
    setValue('haloTimeIntervalMinutes', haloTimeIntervalMinutes)
    updateTimeSearchParams(urlKey, startTime, haloTimeIntervalMinutes)
    updateFormState({ startTime, haloTimeIntervalMinutes })
    updateQuery({ time: startTime?.toString(), halo: haloTimeIntervalMinutes?.toString() })
    hideModal()
  }

  const onModalBackClick = useCallback(() => {
    showModal('viewAllExperienceDates')
  }, [showModal])

  return (
    <>
      <VStack spacing="sm" mt={showTabs ? 'none' : 'm'}>
        <StyledSearchInputGridContainer>
          <Form {...form} onSubmit={() => {}} onInvalid={() => {}}>
            <ReservationSearchComponent
              isDatesFetching={isDatesFetching}
              isDayBlocked={isDayBlocked}
              partySize={field.prop('partySize')}
              onPartySizeChange={onPartySizeChange}
              startDate={field.prop('startDate')}
              onStartDateChange={onStartDateChange}
              startTime={startTime}
              haloTimeIntervalMinutes={haloTimeIntervalMinutes}
              onTimeChange={handleFilterTimeChange}
              requiredDate
            />
          </Form>
        </StyledSearchInputGridContainer>
        <Experiences
          partySize={partySize}
          onPartySizeChange={onExperiencePartySizeChange}
          startDate={DateOnly.fromDate(startDate).toIso()}
          onStartDateChange={onExperienceStartDateChange}
          startTime={startTime}
          haloTimeIntervalMinutes={haloTimeIntervalMinutes}
          experiences={experiences}
          isLoading={isExperiencesFetching}
          isError={isError}
          onTimeSlotClick={onTimeSlotClick}
          onMoreDetailsClick={onMoreDetailsClick}
          onClearTimeFilter={onClearTimeFilter}
          onRequestModalBackClick={onModalBackClick}
          showNextAvailableDay
          showViewAllAvailableDatesButton
        />
      </VStack>
      {activeModal === 'moreExperienceDetails' && selectedExperience && (
        <Window active>
          <ExperienceDetailsModal
            experience={selectedExperience}
            currencyCode={currencyCode}
            timeSlot={selectedTimeSlot}
            onClose={onMoreDetailsModalClose}
            onConfirmSelect={onAvailabilitySelect}
            onBack={previousActiveModal === 'viewAllExperienceDates' ? onModalBackClick : undefined}
          />
        </Window>
      )}
      {activeModal === 'reservationHold' && (
        <Window active>
          <ReservationHoldModal />
        </Window>
      )}
    </>
  )
}
